import React from 'react';
import { motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { showSignInModal, showContactModal } from '../../features/authSlice';
import DemoPhone from './DemoDevice';

// ─── Landing Page Settings ──────────────────────────────────────────────────
// Toggle between the interactive demo device and a hero video.
// Set to true to show the DemoPhone, false to show the video instead.
const SHOW_DEMO_DEVICE = false;

// If SHOW_DEMO_DEVICE is false, this URL will be used for the hero video.
// Supports mp4, webm, or an embed URL (YouTube/Vimeo).
const HERO_VIDEO_URL = '/videos/hero.mp4';
// ────────────────────────────────────────────────────────────────────────────

// Phone-shaped frame matching the in-app device (portrait, notch, home button).
// `width` accepts any CSS length (e.g. '24vh', 'clamp(160px, 22vh, 280px)') so the
// frame scales with the viewport. All inner dimensions are derived via CSS calc().
const PhoneFrame = ({ width = '190px', videoUrl }) => {
  const w = `var(--phone-w)`;
  const bezel = `calc(${w} * 0.021)`;
  const frameRadius = `calc(${w} * 0.143)`;
  const screenRadius = `calc(${w} * 0.122)`;
  const notchWidth = `calc(${w} * 0.34)`;
  const notchHeight = `calc(${w} * 0.067)`;
  const notchRadius = `calc(${w} * 0.04)`;
  const footerHeight = `calc(${w} * 0.16)`;
  const homeButtonSize = `calc(${w} * 0.105)`;
  const homeIconSize = `calc(${w} * 0.058)`;

  return (
    <div
      className="device-bezel"
      style={{
        '--phone-w': width,
        display: 'inline-block',
        width: w,
        aspectRatio: '420 / 850',
        borderRadius: frameRadius,
        padding: bezel,
        boxSizing: 'border-box',
        background:
          'linear-gradient(145deg, rgb(66,66,96) 0%, rgb(46,46,71) 12%, rgb(26,26,46) 25%, rgb(26,26,46) 50%, rgb(18,18,34) 75%, rgb(14,14,28) 100%)',
        boxShadow:
          '0 25px 50px -15px rgba(0,0,0,0.3), 0 8px 32px rgba(0,0,0,0.15), inset 0 2px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.1)',
        border: '1px solid rgba(255,255,255,0.8)',
        lineHeight: 0,
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: screenRadius,
          overflow: 'hidden',
          position: 'relative',
          background: '#000',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Video fills the screen area above the footer */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#000' }}>
          <video
            src={videoUrl}
            autoPlay
            muted
            loop
            playsInline
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </div>

        {/* Notch */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: notchWidth,
            height: notchHeight,
            background: '#000',
            borderBottomLeftRadius: notchRadius,
            borderBottomRightRadius: notchRadius,
            zIndex: 2,
          }}
        />

        {/* Footer with home button */}
        <div
          style={{
            background: '#000',
            height: footerHeight,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottomLeftRadius: screenRadius,
            borderBottomRightRadius: screenRadius,
          }}
        >
          <div
            style={{
              width: homeButtonSize,
              height: homeButtonSize,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(135deg, rgb(0,0,0) 0%, rgb(0,0,0) 50%, rgb(0,0,0) 100%)',
              boxShadow:
                '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.5)',
              border: '1px solid rgba(60,60,80,0.6)',
              lineHeight: 1,
            }}
          >
            <span
              className="material-icons"
              style={{
                color: '#e5e7eb',
                fontSize: homeIconSize,
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
              }}
            >
              home
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const dispatch = useDispatch();

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#0A0F1A]">
      {/* Remove scaling wrapper - use responsive layout instead */}
      <div className="relative w-full h-full">
        {/* Animated gradient background with depth */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 80% 20%, rgba(68, 194, 253, 0.12) 0%, transparent 50%),
              radial-gradient(circle at 20% 80%, rgba(35, 166, 213, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 50% 50%, rgba(68, 194, 253, 0.03) 0%, transparent 70%),
              linear-gradient(180deg, #0A0F1A 0%, #1C2837 100%)
            `,
          }}
        />
        
        {/* Animated mesh gradient overlay */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(68, 194, 253, 0.03) 2px, rgba(68, 194, 253, 0.03) 4px),
              repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(68, 194, 253, 0.03) 2px, rgba(68, 194, 253, 0.03) 4px)
            `,
            backgroundSize: '100px 100px',
            animation: 'meshMove 20s linear infinite',
          }}
        />

        <style>{`
          @keyframes meshMove {
            0% { transform: translate(0, 0); }
            100% { transform: translate(100px, 100px); }
          }
          @keyframes glow {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 0.8; }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          @keyframes buttonGlow {
            0%, 100% { box-shadow: 0 0 20px rgba(68, 194, 253, 0.3); }
            50% { box-shadow: 0 0 40px rgba(68, 194, 253, 0.6); }
          }
          @keyframes caraShimmer {
            0% { background-position: 250% 0; }
            100% { background-position: -150% 0; }
          }
        `}</style>

        {/* Main content container - Flex column on mobile, right aligned on desktop */}
        <div className="relative h-full flex flex-col lg:flex-row items-start lg:items-center justify-start lg:justify-end px-6 py-8 md:px-16 lg:px-24 overflow-y-auto lg:overflow-hidden">
          <div className="max-w-3xl text-center lg:text-right w-full">
            {/* Cara logo with shimmer sweep across the logo's pixels */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center lg:justify-end mb-0 mt-[30px] lg:mt-[-1em]"
              style={{ marginBottom: '25px' }}
            >
              <div className="relative inline-block" style={{ maxWidth: '100%' }}>
                <img
                  src="/images/cara-logo-white-tag-blue.png"
                  alt="Cara — Your bridge to conversational CX"
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    height: 'auto',
                    width: 'clamp(15.3rem, 27.2vw, 30.6rem)',
                    maxWidth: '100%',
                    display: 'block',
                    filter: 'drop-shadow(0 0 24px rgba(68, 194, 253, 0.35))',
                  }}
                />
                {/* Shimmer overlay masked to the logo shape */}
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 2,
                    pointerEvents: 'none',
                    background:
                      'linear-gradient(110deg, transparent 0%, transparent 42%, rgba(68,194,253,0.55) 50%, transparent 58%, transparent 100%)',
                    backgroundSize: '250% 100%',
                    WebkitMaskImage: 'url(/images/cara-logo-white-tag-blue.png)',
                    maskImage: 'url(/images/cara-logo-white-tag-blue.png)',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    mixBlendMode: 'screen',
                    animation: 'caraShimmer 8s ease-in-out infinite',
                  }}
                />
              </div>
            </motion.div>

            {/* Demo Phone - Mobile only, positioned after headline, before subtitle */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="lg:hidden flex items-center justify-center mb-1 w-full overflow-visible"
              style={{
                height: 'auto',
                minHeight: '0',
                marginTop: '20px'
              }}
            >
              {SHOW_DEMO_DEVICE ? (
                <div
                  style={{
                    transform: 'scale(0.45)',
                    transformOrigin: 'center top',
                    filter: 'drop-shadow(0 15px 30px rgba(0, 0, 0, 0.5))',
                    lineHeight: 5,
                    margin: '-90px 0 -445px 0',
                    position: 'relative'
                  }}
                >
                  <DemoPhone scriptUrl="/demo-script.json" />
                </div>
              ) : (
                <div
                  style={{
                    filter: 'drop-shadow(0 15px 30px rgba(0, 0, 0, 0.5))',
                    marginBottom: '20px',
                    lineHeight: 0,
                  }}
                >
                  <PhoneFrame
                    width="clamp(130px, 32vw, 200px)"
                    videoUrl={HERO_VIDEO_URL}
                  />
                </div>
              )}
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-gray-300 mb-4 lg:mb-10 max-w-xl mx-auto lg:mx-0 lg:ml-auto"
              style={{
                fontSize: 'clamp(0.875rem, 1.5vw, 1.125rem)',
                lineHeight: 1.6,
                fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                padding: '10px',
              }}
            >
             Most organizations want AI service agents that can deliver compelling and empathic customer experiences. Few have an easy route to building them with secure and governed workflows. Cara is the bridge.
             </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-wrap gap-4 mb-6 lg:mb-12 justify-center lg:justify-end"
            >
              {/* Try Now button with enhanced glow */}
              <button
                onClick={() => dispatch(showSignInModal())}
                className="px-6 lg:px-8 py-3 lg:py-3.5 bg-gradient-to-r from-[#44C2FD] to-[#23A6D5] text-white font-semibold rounded-lg hover:from-[#3AB0E8] hover:to-[#1E92BA] transition-all duration-300 uppercase relative group overflow-hidden"
                style={{
                  fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)',
                  fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                  letterSpacing: '0.05em',
                  boxShadow: '0 4px 20px rgba(68, 194, 253, 0.3), 0 0 40px rgba(68, 194, 253, 0.1)',
                }}
              >
                <span className="relative z-10">TALK WITH HER</span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </button>

              {/* Contact Us button with enhanced border */}
              <button
                onClick={() => dispatch(showContactModal())}
                className="px-6 lg:px-8 py-3 lg:py-3.5 bg-transparent text-white font-semibold rounded-lg border-2 border-[#44C2FD] hover:bg-[#44C2FD]/20 hover:border-[#3AB0E8] transition-all duration-300 uppercase relative group"
                style={{
                  fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)',
                  fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                  letterSpacing: '0.05em',
                  boxShadow: '0 0 20px rgba(68, 194, 253, 0.15)',
                }}
              >
                <span className="relative z-10">TALK WITH US</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#44C2FD]/0 via-[#44C2FD]/30 to-[#44C2FD]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
              </button>
            </motion.div>

            {/* Trust indicators with enhanced styling */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="space-y-2.5 lg:space-y-3.5"
              style={{ fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif' }}
            > 
              {/* Powered by Pega - visible on all devices */}
              <br />
              <div className="flex flex-col items-center lg:items-end gap-1.5 text-gray-400 text-xs uppercase tracking-wider font-medium">
                <div className="flex items-center gap-2">
                  <span>BUILT ON</span>
                  <img 
                    src="/images/pega.png" 
                    alt="Pega" 
                    className="h-3 lg:h-4"
                    style={{ filter: 'brightness(1.3) drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))' }}
                  />
                  <span className="text-white font-bold">PEGA INFINITY</span>
                  <span>BROUGHT TO LIFE BY</span>
                  <img 
                    src="/images/labb-white.webp" 
                    alt="Labb" 
                    className="h-4 lg:h-5"
                    style={{ filter: 'brightness(1.3) drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))' }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Left side - Device preview with live demo (vertically centered) */}
        <div
          className="max-lg:hidden absolute z-10"
          style={{
            left: 'calc(6% + 50px)',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            style={
              SHOW_DEMO_DEVICE
                ? { transform: 'scale(0.50)', transformOrigin: 'left center' }
                : undefined
            }
          >
            <div
              className="relative"
              style={{
                filter: 'drop-shadow(0 30px 60px rgba(0, 0, 0, 0.7))',
              }}
            >
              {SHOW_DEMO_DEVICE ? (
                <DemoPhone scriptUrl="/demo-script.json" />
              ) : (
                <PhoneFrame
                  width="clamp(216px, 34vh, 384px)"
                  videoUrl={HERO_VIDEO_URL}
                />
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
